import { Box, Chip, Grid, Paper, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useEffect, useState } from 'react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { SCUIButton, SCUICustomerCardModal, SCUILoadingLinear } from '../../../../atomic/atoms';
import { CustomerQuery, PostCheckoutQuery, RefundRestQuery } from '../../../../services/query';
import { useLocation } from 'react-router';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import React from 'react';
import { SCUICustomDataGrid } from '../../../../atomic/molecules';
import TimeHelper from '../../../../utils/time';
import Swal from 'sweetalert2';
import { CLM } from './customerLcwMoney.classes';

const parseDisableLcwMoneyTransferResponse = (res: any, currentCustomerId: number) => {
    const payload = res?.data ?? res;

    if (typeof payload?.disableLcwMoneyTransfer === 'boolean') {
        const isDisabled = payload.disableLcwMoneyTransfer;
        return {
            isDisabled,
            message: isDisabled ? String(payload?.isMessage ?? '').trim() : '',
        };
    }

    const customerIds = payload?.customerID ?? payload?.customerId;

    if (Array.isArray(customerIds)) {
        const isDisabled = customerIds.some((id: number | string) => Number(id) === Number(currentCustomerId));
        return {
            isDisabled,
            message: isDisabled ? String(payload?.isMessage ?? '').trim() : '',
        };
    }

    if (typeof payload === 'boolean') {
        return { isDisabled: payload, message: '' };
    }

    return { isDisabled: false, message: '' };
};

export const CustomerLcwMoney: FC<any> = ({ datas }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [fetchDatas, setFetchDatas] = useState<any>();
    const search = useLocation().search;
    const searchParams = new URLSearchParams(search);
    const [customerId, setCustomerId] = React.useState<number>(0);
    const [modalOpen, setModalOpen] = useState(false);
    const [isTransferDisabled, setIsTransferDisabled] = useState(false);
    const [isTransferRuleLoading, setIsTransferRuleLoading] = useState(false);
    const [transferDisabledMessage, setTransferDisabledMessage] = useState('');
    const onModalClose = () => setModalOpen(false);

    useEffect(() => {
        let customerIdValue;
        if (!datas) {
            customerIdValue = Number(searchParams.get('customerId')) || Number(searchParams.get('CustomerId'));
        } else {
            customerIdValue = datas.customer.customerId;
        }
        setCustomerId(Number(customerIdValue));
    }, []);

    useEffect(() => {
        if (customerId && Number(customerId) > 0) {
            GetWalletTransactionByCustomerId();
            CheckDisableLcwMoneyTransfer(Number(customerId));
        }
    }, [customerId]);

    const CheckDisableLcwMoneyTransfer = (id: number) => {
        setIsTransferRuleLoading(true);
        setIsTransferDisabled(false);
        setTransferDisabledMessage('');
        CustomerQuery.DisableLcwMoneyTransfer({ customerId: id })
            .then((res: any) => {
                const { isDisabled, message } = parseDisableLcwMoneyTransferResponse(res, id);
                setIsTransferDisabled(isDisabled);
                setTransferDisabledMessage(
                    message || (isDisabled ? t('customerInformation.lcwMoneyTransferDisabled') : '')
                );
            })
            .catch((error: any) => {
                console.log('DisableLcwMoneyTransfer Hata :>> ', error);
                setIsTransferDisabled(false);
                setTransferDisabledMessage('');
            })
            .finally(() => {
                setIsTransferRuleLoading(false);
            });
    };

    const LcwMoneyCashRefund = (selectedBankAccount: any) => {
        let customerIdValue;
        if (!datas) {
            customerIdValue = Number(searchParams.get('customerId')) || Number(searchParams.get('CustomerId'));
        } else {
            customerIdValue = datas.customer.customerId;
        }
        if (selectedBankAccount) {
            RefundRestQuery.LcwMoneyCashRefund({
                customerId: customerIdValue,
                customerBankAccountId: selectedBankAccount.bankAccountId,
            }).then((res: any) => {
                if (res && res.isSucceed) {
                    Swal.fire({
                        title: t('general.completed'),
                        icon: 'success',
                        timer: 3000,
                    });
                    onModalClose();
                    GetWalletTransactionByCustomerId();
                } else
                    Swal.fire({
                        title: t('general.failed'),
                        icon: 'error',
                        timer: 5000,
                    });
            });
        } else {
            Swal.fire({
                title: t('customerInformation.requiredBankList'),
                icon: 'error',
                timer: 5000,
            });
        }
    };
    const columns: ExtendedGridColDef[] = [
        {
            field: 'description',
            headerName: t('customerInformation.description'),
            flex: 1,
            hide: false,
            renderCell: (params: any) => {
                return (
                    <div>
                        <Tooltip title={<h1>{params.row.description}</h1>}>
                            <p>
                                {params.row.description?.length > 45
                                    ? params.row.description.slice(0, 45) + ' ...'
                                    : params.row.description}
                            </p>
                        </Tooltip>
                    </div>
                );
            },
        },
        {
            field: 'amount',
            headerName: t('customerInformation.amount'),
            flex: 1,
            renderCell: (params: any) => {
                const raw: string = params?.row?.amount ?? '';
                const [rawValue] = raw.trim().split(' ');
                const numberAmount = parseFloat(rawValue?.replace(',', '.') ?? '0');
                return (
                    <Chip
                        className={CLM.fieldChipAmount}
                        label={params?.row?.amount}
                        sx={{ backgroundColor: numberAmount > 0 ? '#21bf73' : '#dc3545', color: 'white' }}
                    />
                );
            },
        },
        {
            field: 'transactionDate',
            headerName: t('customerInformation.dateandtime'),
            flex: 1,
        },
    ];
    const GetWalletTransactionByCustomerId = () => {
        setLoading(true);
        let params: any = {};
        params.customerId = customerId;
        params.walletTypeId = 1;
        PostCheckoutQuery.GetWalletTransactionByCustomerId(params)
            .then((res: any) => {
                if (res) {
                    let parseData = res.data?.map((item: any) => {
                        return {
                            amount: item.amount + ' ' + item.currencySymbol || '-',
                            description: item.description || '-',
                            transactionDate: TimeHelper.formatDateHour(item.transactionDate) || '-',
                            transactionId: item.transactionId || '-',
                            currencySymbol: item.currencySymbol || '-',
                        };
                    });
                    let req = {
                        parseData: parseData,
                        availableBalance: res.lcwMoney,
                    };
                    setFetchDatas(req);
                }
            })
            .catch((error: any) => {
                console.log('Hata :>> ', error);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const isTransferButtonDisabled = isTransferRuleLoading || isTransferDisabled;

    return (
        <>
            <SCUILoadingLinear display={loading} />
            {customerId && Number(customerId) > 0 && (
                <SCUICustomerCardModal
                    open={modalOpen}
                    callFunction={LcwMoneyCashRefund}
                    onClose={onModalClose}
                    customerId={customerId}
                    buttonText={t('general.transferToBankAccount')}
                />
            )}
            {
                <Paper sx={{ padding: '16px' }} className={CLM.page}>
                    <Grid container sx={{ margin: '1rem' }}>
                        <Grid
                            size={{
                                xl: 12,
                                lg: 12,
                                md: 12,
                                xs: 12,
                            }}
                        >
                            <Grid className={CLM.toolbarRow} style={{ display: 'flex' }}>
                                <Grid
                                    className={CLM.fieldAvailableBalance}
                                    sx={{ marginTop: '1rem' }}
                                    size={{
                                        xl: 6,
                                        lg: 6,
                                        md: 4,
                                        xs: 4,
                                    }}
                                >
                                    <Typography
                                        variant="h1"
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        <h3> {t('customerInformation.availableBalance')} :&nbsp; </h3>{' '}
                                        <h3>
                                            {fetchDatas?.availableBalance} {fetchDatas?.parseData[0]?.currencySymbol}
                                        </h3>
                                    </Typography>
                                </Grid>
                                <Grid
                                    size={{
                                        xl: 6,
                                        lg: 6,
                                        md: 3,
                                        xs: 3,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Tooltip title={isTransferDisabled ? transferDisabledMessage : ''}>
                                            <span>
                                                <SCUIButton
                                                    className={CLM.inputButtonPayMoneyOrder}
                                                    type={'primary'}
                                                    sizeType={'fixed'}
                                                    size={'m'}
                                                    text={t('customerInformation.payViaMoneyOrderEft')}
                                                    disabled={isTransferButtonDisabled}
                                                    onClick={() => setModalOpen(true)}
                                                >
                                                    {' '}
                                                </SCUIButton>
                                            </span>
                                        </Tooltip>
                                        {isTransferDisabled && transferDisabledMessage && (
                                            <Box
                                                className={CLM.transferDisabledInfo}
                                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                            >
                                                <InfoOutlinedIcon color="info" fontSize="small" />
                                                <Typography variant="body2" color="info.main">
                                                    {transferDisabledMessage}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Grid>
                            </Grid>
                            {fetchDatas?.parseData?.length > 0 && (
                                <Grid
                                    sx={{ marginRight: '1.5rem' }}
                                    size={{
                                        xl: 12,
                                        lg: 12,
                                        md: 12,
                                        xs: 12,
                                    }}
                                >
                                    <Box className={CLM.datagrid}>
                                        <SCUICustomDataGrid rows={fetchDatas.parseData} columns={columns} randomId />
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </Paper>
            }
        </>
    );
};
